import { FC, useCallback, useState } from 'react';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Navigate } from 'react-router-dom';
import { ApiError } from 'api/core/ApiError';
import { CommonCompanyProfile } from 'api/models/CommonCompanyProfile';
import { ConfirmationDelete, ConfirmationDialog, FormDialog } from 'components/Dialogs';
import {
  useCreateCommonProfile,
  useDeleteCommonProfile,
  useListCommonProfiles,
  useUpdateCommonProfile,
  useUpdateEdgarIssuerName,
} from 'services/hooks/companies';
import { useUserUtils } from 'utilities/userUtils';
import { TABLE_HEADERS } from './CommonCompanyProfiles.constants';
import CommonCompanyProfileForm from './components/CommonCompanyProfileForm';
import { EMPTY_FORM_STATE , ProfileFormState, SaveConflict, toWritePayload } from './components/CommonCompanyProfileForm/CommonCompanyProfileForm.props';
import FundFamiliesSection from './components/FundFamiliesSection/FundFamiliesSection';
import ProfileMergeDialog from './components/ProfileMergeDialog';

const PAGE_SIZE = 25;

const CommonCompanyProfiles: FC = () => {
  const { isStaffUser, isSuperUser, user } = useUserUtils();
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CommonCompanyProfile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CommonCompanyProfile | null>(null);
  const [formValues, setFormValues] = useState<ProfileFormState>(EMPTY_FORM_STATE);
  const [pendingEdgarAdds, setPendingEdgarAdds] = useState<number[]>([]);
  const [pendingEdgarRemoves, setPendingEdgarRemoves] = useState<number[]>([]);
  const [hasConflicts, setHasConflicts] = useState(false);
  const [saveConflicts, setSaveConflicts] = useState<SaveConflict[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  // Merge state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [mergeSourceProfile, setMergeSourceProfile] = useState<CommonCompanyProfile | null>(null);
  const [mergeSecondaryProfile, setMergeSecondaryProfile] = useState<CommonCompanyProfile | null>(null);
  const [mergeConflictFields, setMergeConflictFields] = useState<string[]>([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  const { data, isLoading } = useListCommonProfiles(search || undefined, page + 1);
  const { data: pickerData } = useListCommonProfiles(pickerSearch || undefined);
  const createMutation = useCreateCommonProfile();
  const updateMutation = useUpdateCommonProfile();
  const deleteMutation = useDeleteCommonProfile();
  const updateEdgarIssuerName = useUpdateEdgarIssuerName();

  const profiles: CommonCompanyProfile[] = data?.results ?? data ?? [];
  const totalCount: number = data?.count ?? profiles.length;

  const pickerProfiles: CommonCompanyProfile[] = (pickerData?.results ?? pickerData ?? []).filter(
    (p: CommonCompanyProfile) => p.id !== mergeSourceProfile?.id,
  );

  const handleOpenCreate = useCallback(() => {
    setEditTarget(null);
    setFormValues(EMPTY_FORM_STATE);
    setPendingEdgarAdds([]);
    setPendingEdgarRemoves([]);
    setHasConflicts(false);
    setSaveConflicts([]);
    setFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((profile: CommonCompanyProfile) => {
    setEditTarget(profile);
    setFormValues({
      name: profile.name ?? '',
      cap_iq_id: profile.cap_iq_id ?? '',
      sacra_domain: profile.sacra_domain ?? '',
      zanbato_id: profile.zanbato_id ?? '',
    });
    setPendingEdgarAdds([]);
    setPendingEdgarRemoves([]);
    setHasConflicts(false);
    setSaveConflicts([]);
    setFormOpen(true);
  }, []);

  const handleOpenDelete = useCallback((profile: CommonCompanyProfile) => {
    setDeleteTarget(profile);
    setDeleteOpen(true);
  }, []);

  const handleOpenPicker = useCallback((profile: CommonCompanyProfile) => {
    setMergeSourceProfile(profile);
    setPickerSearch('');
    setPickerOpen(true);
  }, []);

  const handlePickerSelect = useCallback((profile: CommonCompanyProfile) => {
    setMergeSecondaryProfile(profile);
    setPickerOpen(false);
    setMergeConflictFields([]);
    setMergeDialogOpen(true);
  }, []);

  const handleCloseMerge = useCallback(() => {
    setMergeDialogOpen(false);
    setMergeSourceProfile(null);
    setMergeSecondaryProfile(null);
    setMergeConflictFields([]);
  }, []);

  const handleMergeSuccess = useCallback(() => {
    setMergeDialogOpen(false);
    setMergeSourceProfile(null);
    setMergeSecondaryProfile(null);
    setMergeConflictFields([]);
    setFormOpen(false);
    setEditTarget(null);
  }, []);

  const handleSave = useCallback(async () => {
    const payload = toWritePayload(formValues);
    try {
      if (editTarget?.id != null) {
        await updateMutation.mutateAsync({ id: editTarget.id, data: payload });
        await Promise.all([
          ...pendingEdgarAdds.map(id =>
            updateEdgarIssuerName.mutateAsync({ id, data: { common_profile: editTarget.id || null } }),
          ),
          ...pendingEdgarRemoves.map(id =>
            updateEdgarIssuerName.mutateAsync({ id, data: { common_profile: null } }),
          ),
        ]);
        enqueueSnackbar('Profile updated successfully', { variant: 'success' });
      } else {
        const newProfile = await createMutation.mutateAsync(payload);
        if (newProfile?.id != null && pendingEdgarAdds.length > 0) {
          const newProfileId = newProfile.id;
          await Promise.all(
            pendingEdgarAdds.map(id =>
              updateEdgarIssuerName.mutateAsync({ id, data: { common_profile: newProfileId } }),
            ),
          );
        }
        enqueueSnackbar('Profile created successfully', { variant: 'success' });
      }
      setFormOpen(false);
    } catch (err) {
      // The request template uses Axios directly, so errors arrive as Axios errors
      // with .response.status / .response.data rather than ApiError's .status / .body.
      const axiosErr = err as any;
      const errStatus = axiosErr?.response?.status ?? (err as ApiError)?.status;
      const errBody = axiosErr?.response?.data ?? (err as ApiError)?.body;
      if (errStatus === 409 && errBody?.conflicts?.length > 0) {
        setSaveConflicts(errBody.conflicts as SaveConflict[]);
      } else {
        enqueueSnackbar('Failed to save profile. Please try again.', { variant: 'error' });
      }
      throw err;
    }
  }, [formValues, editTarget, updateMutation, pendingEdgarAdds, pendingEdgarRemoves, enqueueSnackbar, updateEdgarIssuerName, createMutation]);

  const handleDelete = useCallback(async () => {
    if (deleteTarget?.id != null) {
      await deleteMutation.mutateAsync(deleteTarget.id);
    }
    setDeleteOpen(false);
  }, [deleteTarget, deleteMutation]);

  const isMutating = createMutation.isPending || updateMutation.isPending;

  if (!user) {
    return null;
  }
  if (!isStaffUser() && !isSuperUser()) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Common Company Profiles
      </Typography>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Profiles" />
        <Tab label="Mutual Fund Families" />
      </Tabs>

      {activeTab === 0 && (
        <>
          <Toolbar disableGutters sx={{ mb: 2, gap: 2 }}>
            <TextField
              label="Search by name or Cap IQ ID"
              size="small"
              value={search}
              onChange={e => {
                setSearch(e.target.value); setPage(0);
              }}
              sx={{ width: 320 }}
            />
            <Button variant="contained" onClick={handleOpenCreate}>
              Add Profile
            </Button>
          </Toolbar>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {TABLE_HEADERS.map(h => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEADERS.length} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && profiles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={TABLE_HEADERS.length} align="center">
                      No profiles found.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && profiles.map(profile => (
                  <TableRow key={profile.id} hover>
                    <TableCell>{profile.name}</TableCell>
                    <TableCell>{profile.cap_iq_id ?? '—'}</TableCell>
                    <TableCell>{profile.sacra_domain ?? '—'}</TableCell>
                    <TableCell>{profile.zanbato_id ?? '—'}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenEdit(profile)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenPicker(profile)} title="Merge with another profile">
                        <CallMergeIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDelete(profile)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_, newPage) => setPage(newPage)}
            />
          </TableContainer>
        </>
      )}

      {activeTab === 1 && <FundFamiliesSection />}

      <FormDialog
        open={formOpen}
        title={editTarget ? 'Edit Profile' : 'Add Profile'}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
        isValid={
          formValues.name.trim().length > 0 &&
          !hasConflicts &&
          saveConflicts.length === 0 &&
          (editTarget != null || !!(formValues.cap_iq_id || formValues.sacra_domain || formValues.zanbato_id))
        }
        isLoading={isMutating}>
        <CommonCompanyProfileForm
          values={formValues}
          onChange={vals => {
            setSaveConflicts([]);
            setFormValues(vals);
          }}
          isLoading={isMutating}
          profileId={editTarget?.id}
          onPendingEdgarAddsChange={setPendingEdgarAdds}
          onPendingEdgarRemovesChange={setPendingEdgarRemoves}
          onHasConflicts={setHasConflicts}
          saveConflicts={saveConflicts}
        />
      </FormDialog>

      <ConfirmationDialog
        open={deleteOpen}
        title="Delete Profile"
        onClose={() => setDeleteOpen(false)}
        isLoading={deleteMutation.isPending}
        actions={[
          {
            label: 'Delete',
            variant: 'contained',
            type: 'danger',
            callback: handleDelete,
          },
        ]}>
        <ConfirmationDelete itemName={deleteTarget?.name ?? ''} />
      </ConfirmationDialog>

      {/* Profile picker dialog */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Select a profile to merge with{mergeSourceProfile ? ` "${mergeSourceProfile.name}"` : ''}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Search profiles"
            size="small"
            fullWidth
            value={pickerSearch}
            onChange={e => setPickerSearch(e.target.value)}
            sx={{ mb: 1, mt: 0.5 }}
          />
          <List dense>
            {pickerProfiles.map(p => (
              <ListItemButton key={p.id} onClick={() => handlePickerSelect(p)}>
                <ListItemText
                  primary={p.name}
                  secondary={p.cap_iq_id ?? undefined}
                />
              </ListItemButton>
            ))}
            {pickerProfiles.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No profiles found.
              </Typography>
            )}
          </List>
        </DialogContent>
      </Dialog>

      {/* Merge editor dialog */}
      {mergeDialogOpen && mergeSourceProfile && mergeSecondaryProfile && (
        <ProfileMergeDialog
          open={mergeDialogOpen}
          primaryProfile={mergeSourceProfile}
          secondaryProfile={mergeSecondaryProfile}
          conflictFields={mergeConflictFields}
          onClose={handleCloseMerge}
          onSuccess={handleMergeSuccess}
        />
      )}

    </Box>
  );
};

export default CommonCompanyProfiles;
